import { fitBounds } from '@math.gl/web-mercator';
import debounce from 'lodash/debounce';
import flatten from 'lodash/flatten';
import { LngLat } from 'mapbox-gl';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapRef, MapWheelEvent, ViewState, ViewStateChangeEvent } from 'react-map-gl';
import { useSelector } from 'react-redux';

import { MapCarouselPopup } from '@/components/route/search/MapCarouselPopup';
import SearchMapMarkers from '@/components/route/search/SearchMapMarkers';
import MapDynamic from '@/components/ui/MapDynamic/MapDynamic';
import { TCurrency } from '@/config/locales';
import { PADDING, ZOOM } from '@/constants/map';
import { getItemsPerPage } from '@/constants/pagination';
import { ESearchFilters } from '@/constants/searchFilters';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useIsFirstRender } from '@/hooks/useIsFirstRender';
import usePrevious from '@/hooks/usePrevious';
import useQueryParams from '@/hooks/useQueryParams';
import { useRetrieveMapboxPlace } from '@/hooks/useRetrieveMapboxPlace';
import { useReverseGeocoding } from '@/hooks/useReverseGeocoding';
import { TRootState } from '@/redux/rootReducer';
import {
  lastMapPan,
  lastZoomDirection,
} from '@/redux/selectors/analytics-selectors/search/searchViewedEventData';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { IMapMarkerData, TBounds } from '@/redux/selectors/search/map';
import { getSearchResultsMeta } from '@/redux/selectors/search/meta';
// ANALYTICS
import {
  trackListingMarkerPreviewedEvent,
  trackSearchAsMapMoveEvent,
} from '@/services/analytics/maps';
import { EMapStatus } from '@/services/analytics/maps/types';
import { ERentalType } from '@/services/analytics/types';
import { trackEvent } from '@/services/track-event';
import { logger } from '@/utility/logger';
import { isCampgroundTile, mapCampgroundTileToRentalPrice } from '@/utility/mapSearchResultToTile';

type TSearchMeta = TRootState['search']['meta'];

export type TViewportChangeBounds = {
  [ESearchFilters.BOUNDS_NE]: string;
  [ESearchFilters.BOUNDS_SW]: string;
};

interface IProps {
  isLoading: boolean;
  recalculateMapViewport: boolean;
  currency: TCurrency;
  bounds: TBounds;
  markers: IMapMarkerData[];
  searchMeta: TSearchMeta;
  onViewportChange: (bounds: TViewportChangeBounds, ignoreRecalculateMapViewport?: boolean) => void;
  isDesktopMapViewExpanded: boolean;
  toggleExpandMapView: () => void;
  isOnPromoPage: boolean;
  allowPromoPageViewportChange?: boolean;
  promoSlug?: string;
  hasNavigation: boolean;
  hasMapLayers: boolean;
  isOnWishlist: boolean;
  isOnListingPage: boolean;
}
interface IGetViewportPropsArg {
  mapDimensions: { width: number; height: number };
  bounds: TBounds;
  searchMeta: TSearchMeta;
  zoom?: number;
  isAboveXL?: boolean;
}

type Viewport = Omit<ViewState, 'pitch' | 'bearing' | 'padding'>;

const getViewportProps = ({
  mapDimensions,
  bounds,
  searchMeta,
  zoom,
  isAboveXL,
}: IGetViewportPropsArg): { longitude: number; latitude: number; zoom: number } => {
  // In case fitBounds fail, fallback to the searchMeta lat/lng or default to 0,0
  const boundsBasedViewport = (() => {
    try {
      if (bounds.length) {
        return fitBounds({
          ...mapDimensions,
          padding: PADDING[isAboveXL ? 'desktop' : 'mobile'],
          bounds,
        });
      }
    } catch {}

    return searchMeta?.lat && searchMeta?.lng
      ? { latitude: searchMeta?.lat, longitude: searchMeta?.lng }
      : { latitude: 0, longitude: 0 };
  })();

  const viewport = {
    zoom: zoom || ZOOM.DEFAULT,
    ...mapDimensions,
    ...boundsBasedViewport,
  };

  return viewport;
};

// Compare two bounds and, given a lat/lng threshold, return if they're equal or not
const areBoundsEqual = (a: TBounds | undefined, b: TBounds | undefined, threshold = 0.005) => {
  const compareBound = (a: number | undefined, b: number | undefined) =>
    Math.abs((a ?? 0) - (b ?? 0)) < threshold;

  return (
    compareBound(a?.[0]?.[0], b?.[0]?.[0]) &&
    compareBound(a?.[0]?.[1], b?.[0]?.[1]) &&
    compareBound(a?.[1]?.[0], b?.[1]?.[0]) &&
    compareBound(a?.[1]?.[1], b?.[1]?.[1])
  );
};

export const wrapBounds = (bounds: TBounds): TBounds => {
  if (!bounds[0] || !bounds[1]) return [];
  const b1 = new LngLat(bounds[0][0], bounds[0][1]).wrap();
  const b2 = new LngLat(bounds[1][0], bounds[1][1]).wrap();
  return [
    [b1.lng, b1.lat],
    [b2.lng, b2.lat],
  ];
};

const getTrackInfoOfSelectedMarker = (id: number, markers: IMapMarkerData[]) => {
  const rentalsOnMap = flatten(markers.map(marker => [...marker.rentals]));
  const selectedRental = rentalsOnMap.find(rental => rental?.id === id);

  if (!selectedRental?.price) return;

  const isCampground = isCampgroundTile(selectedRental);
  const rentalPrice = isCampground
    ? mapCampgroundTileToRentalPrice(selectedRental)
    : selectedRental.price;

  return {
    isInstabook: rentalPrice.instantBook,
    discountPercentOff: rentalPrice.discount,
    pricePerNight: rentalPrice.currentPrice / 100,
    basePricePerNight: rentalPrice.originalPrice / 100,
    rentalType: (() => {
      if (isCampground) return ERentalType.CAMPGROUND;
      if (selectedRental.isCampsite)
        return selectedRental.campsiteCategoryType || ERentalType.CAMPSITE;
      if (selectedRental.isStay) return ERentalType.STAY;
      return ERentalType.RV;
    })() as ERentalType,
    isCampground: isCampground || !!selectedRental.isCampsite,
  };
};

const EVENT_MAP_FIT_BOUNDS = 'map_fit_bounds';

const SearchMap: React.FC<IProps> = ({
  isLoading,
  recalculateMapViewport,
  currency,
  bounds,
  markers,
  searchMeta,
  onViewportChange,
  isDesktopMapViewExpanded,
  toggleExpandMapView,
  isOnPromoPage,
  allowPromoPageViewportChange,
  promoSlug,
  hasNavigation,
  hasMapLayers,
  isOnWishlist,
  isOnListingPage,
}) => {
  const isFirstRender = useIsFirstRender();
  const queryParams = useQueryParams();
  const curCategory = queryParams[ESearchFilters.RENTAL_CATEGORY];
  const prevCategory = usePrevious(curCategory);
  const { isAboveDesktop, isAboveXL } = useBreakpoint();
  const mapBoundsRef = useRef(bounds);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapRef>(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [canRecalcViewport, setCanRecalcViewport] = useState(false);
  const [dynamicSearchEnabled, setDynamicSearchEnabled] = useState(true);
  const prevDynamicSearchEnabled = usePrevious(dynamicSearchEnabled);
  const [mapDimensions, setMapDimensions] = useState({ height: 1024, width: 390 });
  const [viewport, setViewport] = useState<Viewport>(
    getViewportProps({ mapDimensions, bounds, searchMeta, isAboveXL }),
  );
  const [openedMarker, setOpenedMarker] = useState<number | null>(null);
  const isSearchLoading = useSelector<TRootState, boolean>(
    (state: TRootState) => state.search.isLoading,
  );

  const mapStatus = isAboveXL
    ? isDesktopMapViewExpanded
      ? EMapStatus.EXPANDED
      : EMapStatus.COLLAPSED
    : null;

  useEffect(() => {
    if (isFirstRender) return;
    if (!isSearchLoading) {
      setCanRecalcViewport(false);
    } else {
      setCanRecalcViewport(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchLoading]);

  const getPaddedBounds = useCallback(
    (map: mapboxgl.Map, oldBounds?: TBounds): TBounds => {
      const container = map.getContainer();
      if (!container.clientHeight && !container.clientWidth && oldBounds?.length) {
        // prevent bug from 0 height/0 width container
        return oldBounds;
      }

      const padding = PADDING[isAboveXL ? 'desktop' : 'mobile'];
      const SW = map.unproject([padding.left, container.clientHeight - padding.bottom]);
      const NE = map.unproject([container.clientWidth - padding.right, padding.top]);
      return [
        [SW.lng, SW.lat],
        [NE.lng, NE.lat],
      ];
    },
    [isAboveXL],
  );

  const { stopPosition, addressLat, addressLng } = useSelector(getSearchResultsMeta);

  const { [ESearchFilters.PAGE_LIMIT]: pageLimit, [ESearchFilters.MAPBOX_ID]: mapboxId } =
    useSelector(getQueryParams);
  const itemsPerPage = getItemsPerPage(pageLimit);
  const currentPageViewed = Math.ceil(stopPosition / itemsPerPage);

  const { place: placeFromReverseGeocoding } = useReverseGeocoding({
    lat: mapboxId ? undefined : addressLat,
    lon: mapboxId ? undefined : addressLng,
  });
  const { place: placeFromMapboxId } = useRetrieveMapboxPlace(
    typeof mapboxId === 'string' ? mapboxId : undefined,
  );

  const coordinates = useMemo(() => {
    if (mapboxId) {
      return {
        lat: placeFromMapboxId?.geometry.coordinates[1],
        lon: placeFromMapboxId?.geometry.coordinates[0],
      };
    }
    return { lat: addressLat, lon: addressLng };
  }, [mapboxId, placeFromMapboxId, addressLat, addressLng]);

  // Update map's dimensions based on container
  useEffect(() => {
    if (!containerRef?.current) {
      return;
    }

    const width = containerRef.current.offsetWidth;
    const height = containerRef.current.offsetHeight;

    if (width > 0 && height > 0) {
      setMapDimensions({
        width,
        height,
      });
    } else {
      const error = new Error('SearchMap.tsx containerRef dimension is 0');
      logger.captureExceptionWithDatadog(error, {
        containerRefCurrent: containerRef.current,
        width,
        height,
      });
    }
  }, []);

  const searchByMapBounds = useCallback(
    (forceSearch = false) => {
      const map = mapRef.current?.getMap();
      const prevBounds = mapBoundsRef.current;
      if (!map) return;

      const paddedBounds = getPaddedBounds(map);
      const wrappedBounds = wrapBounds(paddedBounds);

      if (!areBoundsEqual(prevBounds, paddedBounds) || forceSearch) {
        if (lastMapPan() === null) {
          lastMapPan(1);
        }
        const query = {
          [ESearchFilters.BOUNDS_SW]: `${wrappedBounds[0]?.[1]},${wrappedBounds[0]?.[0]}`,
          [ESearchFilters.BOUNDS_NE]: `${wrappedBounds[1]?.[1]},${wrappedBounds[1]?.[0]}`,
          [ESearchFilters.NEAR]: '',
          [ESearchFilters.PAGE_LIMIT]: '',
          [ESearchFilters.PAGE_OFFSET]: String(0),
        };
        onViewportChange?.(query, true);
        mapBoundsRef.current = paddedBounds;
      }
    },
    [onViewportChange, mapRef, mapBoundsRef, getPaddedBounds],
  );

  // Disabling eslint here because it doesn't like the usage of non inlined functions,
  // but is necessary to use debounce here to prevent unnecessary API calls.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchByMapBoundsDebounced = useCallback(debounce(searchByMapBounds, 300), [
    searchByMapBounds,
  ]);

  const updateViewport = useCallback<
    (viewport: { latitude: number; longitude: number; zoom: number }, forceSearch?: boolean) => void
  >(
    (newViewport, forceSearch) => {
      setViewport(newViewport);
      setHasUserInteracted(true);
      if (!(dynamicSearchEnabled && isAboveDesktop)) return;
      searchByMapBoundsDebounced(forceSearch);
    },
    [dynamicSearchEnabled, isAboveDesktop, searchByMapBoundsDebounced],
  );

  // On change of viewport, query new rentals based on new boundaries
  const handleViewportChange = useCallback<(e: ViewStateChangeEvent) => void>(
    e => {
      updateViewport(e.viewState);
    },
    [updateViewport],
  );

  const handleChangeViewportBounds = (newBounds: [[number, number], [number, number]]) => {
    updateViewport({ ...viewport, ...fitBounds({ ...mapDimensions, bounds: newBounds }) }, true);
  };

  const handleChangeViewportBoundsRef = useRef(handleChangeViewportBounds);
  handleChangeViewportBoundsRef.current = handleChangeViewportBounds;

  // When dynamic search is enabled again, updates the results with
  // rentals in the map's current area
  useEffect(() => {
    if (hasUserInteracted && isAboveDesktop && dynamicSearchEnabled && !prevDynamicSearchEnabled) {
      searchByMapBounds(true);
    }
  }, [
    hasUserInteracted,
    isAboveDesktop,
    dynamicSearchEnabled,
    prevDynamicSearchEnabled,
    searchByMapBounds,
  ]);

  // Force a re-search when we switch tabs between RV and Stay on mobile.
  useEffect(() => {
    if (!isFirstRender && curCategory !== prevCategory && !isAboveDesktop) {
      searchByMapBounds(true);
    }
  }, [curCategory, prevCategory, searchByMapBounds, isAboveDesktop, isFirstRender]);

  // Users can toggle if they want the results to be updated
  // as they move the map
  const toggleDynamicSearch = useCallback(() => {
    const isChecked = !dynamicSearchEnabled;
    setDynamicSearchEnabled(isChecked);
    trackSearchAsMapMoveEvent({ isEnabled: isChecked });
    trackEvent({
      event: 'search/map',
      action: 'toggle-search-as-i-move',
      before: !isChecked,
      after: isChecked,
    });
  }, [setDynamicSearchEnabled, dynamicSearchEnabled]);

  useEffect(() => {
    if (isFirstRender || !(bounds.length && searchMeta) || isDesktopMapViewExpanded) {
      return;
    }

    let updateMapBounds;
    if (isAboveDesktop) {
      updateMapBounds = canRecalcViewport && (!hasUserInteracted || recalculateMapViewport);
    } else {
      updateMapBounds = !hasUserInteracted || recalculateMapViewport;
    }

    const map = mapRef.current?.getMap();
    const newBounds = updateMapBounds || !map ? bounds : getPaddedBounds(map, bounds);
    const zoom = updateMapBounds ? undefined : map?.getZoom();
    mapBoundsRef.current = newBounds;
    setViewport(
      getViewportProps({
        mapDimensions,
        searchMeta,
        zoom,
        bounds: newBounds,
        isAboveXL,
      }),
    );

    // Leaving out isFirstRender intentionally
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [
    recalculateMapViewport,
    mapDimensions,
    bounds,
    searchMeta,
    hasUserInteracted,
    isAboveXL,
    isAboveDesktop,
    isDesktopMapViewExpanded,
  ]);

  const handleMarkerClick = useCallback(
    (id: number | null) => {
      setOpenedMarker(id);
      if (id) {
        trackEvent({
          event: 'search/map',
          action: 'click-on-marker',
          rental: id,
        });
      }

      if (!id) return;
      const trackInfoOfSelectedMarker = getTrackInfoOfSelectedMarker(id, markers);
      if (!trackInfoOfSelectedMarker) return;
      trackListingMarkerPreviewedEvent({
        rentalID: id,
        currentPageViewed,
        markersOnMap: markers.length,
        mapStatus,
        ...trackInfoOfSelectedMarker,
      });
    },
    [setOpenedMarker, currentPageViewed, markers, mapStatus],
  );

  const setWheel = (e: MapWheelEvent) => {
    if (!e.originalEvent) return;

    // get around type error with the cast
    const wheelSourceEvent = e.originalEvent;

    if (wheelSourceEvent.deltaY) {
      if (wheelSourceEvent.deltaY > 0) {
        lastZoomDirection(-1);
      }

      if (wheelSourceEvent.deltaY < 0) {
        lastZoomDirection(1);
      }
    }
  };

  useEffect(() => {
    const handleMapFitBounds = (event: CustomEvent) => {
      if (event.detail) {
        handleChangeViewportBoundsRef.current(event.detail);
      }
    };

    document.addEventListener(EVENT_MAP_FIT_BOUNDS, handleMapFitBounds as EventListener);
    return () => {
      document.removeEventListener(EVENT_MAP_FIT_BOUNDS, handleMapFitBounds as EventListener);
    };
  }, []);

  const openedMarkerData = useMemo(
    () => markers.find(marker => marker.rentals[0]?.id === openedMarker),
    [markers, openedMarker],
  );

  return (
    <div className="w-full h-full xl:absolute" ref={containerRef}>
      <MapDynamic
        hasNavigation={hasNavigation}
        viewport={viewport}
        loading={isLoading}
        searchEnabled={dynamicSearchEnabled}
        toggleSearch={toggleDynamicSearch}
        onViewportChange={handleViewportChange}
        onSearchMapButtonClick={() => searchByMapBounds(true)}
        ref={mapRef}
        onClick={() => setOpenedMarker(null)}
        isDesktopMapViewExpanded={isDesktopMapViewExpanded}
        toggleExpandMapView={toggleExpandMapView}
        onWheel={e => setWheel(e)}
        isOnPromoPage={isOnPromoPage}
        allowPromoPageViewportChange={allowPromoPageViewportChange}
        hasMapLayers={hasMapLayers}
        isOnListingPage={isOnListingPage}
        bottomPopup={
          <MapCarouselPopup
            marker={openedMarkerData}
            promoSlug={promoSlug}
            isOnWishlist={isOnWishlist}
          />
        }>
        <SearchMapMarkers
          container={containerRef.current}
          currency={currency}
          markers={markers}
          openedMarker={openedMarker}
          onMarkerClick={handleMarkerClick}
          promoSlug={promoSlug}
          isOnWishlist={isOnWishlist}
          // For address marker
          addressLat={coordinates.lat}
          addressLng={coordinates.lon}
          place={placeFromMapboxId || placeFromReverseGeocoding}
          isRetrieved={!!placeFromMapboxId}
        />
      </MapDynamic>
    </div>
  );
};

export default SearchMap;
